import { WsRequest } from 'APP/wsApi/wsAppApi/requests/request.types';
import {
  Chat,
  Chat_Role,
  Chat_Type,
  PersonalizedChat,
  UserChatDraftInput,
  ChatParticipant,
} from 'APP/wsApi/wsAppApi/types/chat/chat_model_pb';
import { SetChatMessagesHistoryTtlRequest } from 'APP/wsApi/wsAppApi/types/chat/chat_requests_pb';

import { mapUserMention } from '../common/commonModel.b2b.mapper';
import { mapPersonalizedUserResponse } from '../user/userModel.b2b.mapper';
import {
  GroupType,
  GroupUserRole,
  IDraftInput,
  IGroup,
  IPersonalizedGroup,
  IParticipan,
  ISetChatMessagesHistoryTtlRequest,
} from './groupModel.types';

export function mapChatTypeToGroupType(type: Chat_Type): GroupType {
  switch (type) {
    case Chat_Type.SYSTEM:
      return GroupType.System;
    case Chat_Type.CLOSED_GROUP:
      return GroupType.Closed;
    case Chat_Type.OPEN_GROUP:
      return GroupType.Open;
    case Chat_Type.CLOSED_CHANNEL:
      return GroupType.ChannelClosed;
    case Chat_Type.OPEN_CHANNEL:
      return GroupType.ChannelOpen;
    case Chat_Type.P2P:
      return GroupType.P2P;
    case Chat_Type.SAVED_MESSAGES:
      return GroupType.MySavedMessages;
    case Chat_Type.THREAD:
      return GroupType.Thread;
    case Chat_Type.SECRET_GROUP:
      return GroupType.SecretGroup;
    case Chat_Type.SERVICE_MESSAGES:
      return GroupType.ServiceMessages;
  }
}

export function mapGroupTypeToChatType(type: GroupType): Chat_Type {
  switch (type) {
    case GroupType.Closed:
      return Chat_Type.CLOSED_GROUP;
    case GroupType.Open:
      return Chat_Type.OPEN_GROUP;
    case GroupType.ChannelClosed:
      return Chat_Type.CLOSED_CHANNEL;
    case GroupType.ChannelOpen:
      return Chat_Type.OPEN_CHANNEL;
    case GroupType.P2P:
      return Chat_Type.P2P;
    case GroupType.MySavedMessages:
      return Chat_Type.SAVED_MESSAGES;
    case GroupType.Thread:
      return Chat_Type.THREAD;
    case GroupType.SecretGroup:
      return Chat_Type.SECRET_GROUP;
    default:
      return Chat_Type.SYSTEM;
  }
}

export function mapChatRole(role?: Chat_Role): GroupUserRole {
  switch (role) {
    case Chat_Role.OWNER:
      return GroupUserRole.Owner;
    case Chat_Role.ADMIN:
      return GroupUserRole.Admin;
    case Chat_Role.MEMBER:
      return GroupUserRole.Member;
    case Chat_Role.GUEST:
      return GroupUserRole.Guest;
    default:
      return GroupUserRole.Guest;
  }
}

export function mapChatResponse(chat: Chat): IGroup {
  return {
    id: chat.id.toString(),
    avatarUrl: chat.avatarUrl,
    pinnedMessageIds: chat.pinnedMessageIds.map((id) => id.toString()),
    deepLinkInfo: {
      deepLink: chat.deepLinkInfo?.deepLink || '',
      deepLinkAlias: chat.deepLinkInfo?.deepLinkAlias || '',
      deepLinkQrUrl: chat.deepLinkInfo?.deepLinkQrUrl,
    },
    deleted: false,
    description: chat.description,
    descriptionMentions: chat.descriptionMentions
      ? chat.descriptionMentions.map((item) => mapUserMention(item))
      : [],
    name: chat.name,
    type: mapChatTypeToGroupType(chat.type),
    keywordFilters: 0,
    memberCustomPermissions: {
      canDisplayMembers: true,
      canSendMessages: true,
      canInitCall: true,
      canInitConference: true,
      canInviteUsers: true,
      canLeaveComment: true,
      canPinMessage: false,
      supportedMediaTypes: {
        canSendAudio: true,
        canSendFile: true,
        canSendImage: true,
        canSendLink: true,
        canSendSticker: true,
        canSendVideo: true,
        canSendVoice: true,
        cansSendCircleVideo: true,
      },
      updatedAt: '0',
    },
    obsceneFilterEnabled: false,
    usersCount: chat.usersCount,
    verified: false,
    ttlPeriodMs: chat.ttlPeriodMs ? Number(chat.ttlPeriodMs) : 0,
    activeChatAccessRequestCount: chat.activeChatAccessRequestCount,
  };
}

export function mapUserChatDraftInput(data: UserChatDraftInput): IDraftInput {
  return {
    groupId: data.chatId.toString(),
    updateTs: Number(data.updateTs),
    text: data.text,
  };
}

export function mapPersonalizedChatResponse(
  personalizedChat: PersonalizedChat
): IPersonalizedGroup {
  return {
    groupResponse: mapChatResponse(personalizedChat.chat!),
    botOpponent: undefined,
    draftInput: personalizedChat.draftInput
      ? mapUserChatDraftInput(personalizedChat.draftInput)
      : undefined,
    opponent: personalizedChat.opponent
      ? mapPersonalizedUserResponse(personalizedChat.opponent)
      : undefined,
    userGroupRole: mapChatRole(personalizedChat.role),
    muted: personalizedChat.muted,
    pinOrder: personalizedChat.pinOrder,
    unread: personalizedChat.unread,
  };
}

export function mapChatParticipant(data: ChatParticipant): IParticipan {
  return {
    userId: String(data.user!.user!.id),
    role: mapChatRole(data.role),
  };
}

export function mapSetChatMessagesHistoryTtlRequest(
  data: ISetChatMessagesHistoryTtlRequest
): WsRequest<SetChatMessagesHistoryTtlRequest> {
  return {
    chatId: BigInt(data.groupId),
    periodMs: data.ttlPeriodMs ? BigInt(data.ttlPeriodMs) : undefined,
  };
}
